<template>
  <ComponentHeader title="Relationship Manager" subtitle="Manage parent-child relationships between entities" />

  <div class="entity-relationship-manager p-4 space-y-4">
    <!-- Parent Entity Selection -->
    <div class="bg-white rounded-lg border border-gray-200 p-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Select Parent Entity</h3>

      <div v-if="!selectedParent" class="flex items-center gap-4">
        <select
          v-model="selectedEntityTypeId"
          class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4 min-w-[200px]"
        >
          <option value="">Entity Type...</option>
          <option v-for="et in entityTypes" :key="et.id" :value="et.id">
            {{ et.type_name }}
          </option>
        </select>

        <div v-if="selectedEntityTypeId" class="flex-1 relative" v-click-outside="() => entityDropdownOpen = false">
          <input
            v-model="entitySearch" data-demo-search
            :disabled="loadingEntitiesOfType"
            :placeholder="loadingEntitiesOfType ? 'Loading...' : 'Search entities...'"
            class="sensitive w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4 disabled:opacity-50"
            @focus="entityDropdownOpen = true"
          />
          <div
            v-if="entityDropdownOpen && filteredEntitiesOfType.length > 0"
            class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            <button
              v-for="entity in filteredEntitiesOfType"
              :key="entity.id"
              class="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors"
              :class="{ 'bg-blue-50 font-medium': selectedParent && selectedParent.id === entity.id }"
              @mousedown.prevent="selectParent(entity)"
            >
              <span class="sensitive">{{ entity.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="selectedParent" class="mt-4 flex items-center gap-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
        <div class="flex-1">
          <div class="text-xs text-blue-500 uppercase tracking-wider font-medium">Selected</div>
          <div class="sensitive font-medium text-gray-900">{{ selectedParent.name }}</div>
          <div class="text-sm text-gray-500">{{ selectedParent.entity_type_name }}</div>
        </div>
        <button
          @click="clearParent"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          title="Clear selection"
        >
          <FontAwesomeIcon :icon="['fas', 'times']" />
        </button>
      </div>
    </div>

    <!-- Children List -->
    <div v-if="selectedParent" class="bg-white rounded-lg border border-gray-200 p-6">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold text-gray-900">
          Children of: <span class="sensitive">{{ selectedParent.name }}</span>
        </h3>
        <EntitySearchSingle
          button-text="Add Children"
          modal-title="Select Child Entities"
          :multiple="true"
          :exclude-ids="children.map(c => c.id)"
          @entities-selected="onChildrenSelected"
        />
      </div>

      <!-- Loading State -->
      <LoadingIndicator v-if="loadingChildren" message="Loading relationships..." />

      <!-- Empty State -->
      <div v-else-if="children.length === 0" class="text-center py-12">
        <FontAwesomeIcon :icon="['fas', 'inbox']" class="text-4xl text-gray-300 mb-3" />
        <p class="text-gray-500">No children assigned to this entity</p>
        <p class="text-sm text-gray-400 mt-1">Click "Add Children" to assign child entities</p>
      </div>

      <!-- Children Table -->
      <div v-else class="space-y-2">
        <div class="flex justify-between items-center mb-3">
          <div class="text-sm text-gray-600">
            {{ children.length }} {{ children.length === 1 ? 'child' : 'children' }}
          </div>
          <button
            v-if="selectedChildrenForRemoval.length > 0"
            @click="bulkRemoveChildren"
            class="btn-danger-sm"
          >
            <FontAwesomeIcon :icon="['fas', 'trash']" />
            Remove Selected ({{ selectedChildrenForRemoval.length }})
          </button>
        </div>

        <div class="border border-gray-200 rounded-lg overflow-hidden">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    :checked="selectedChildrenForRemoval.length === children.length && children.length > 0"
                    @change="toggleSelectAll"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                  />
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="child in children"
                :key="child.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    :checked="selectedChildrenForRemoval.includes(child.id)"
                    @change="toggleChildSelection(child.id)"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                  />
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="sensitive text-sm font-medium text-gray-900">{{ child.name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500">{{ child.entity_type_name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <button
                    @click="promptRemoveChild(child)"
                    class="text-red-500 hover:text-red-700 text-sm transition-colors"
                  >
                    <FontAwesomeIcon :icon="['fas', 'unlink']" class="mr-1" />
                    Remove
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <ConfirmationModal
      :is-visible="!!removeTarget"
      :message="removeMessage"
      @confirm="confirmRemove"
      @cancel="removeTarget = null"
    />

    <!-- Error Display -->
    <div
      v-if="errorMessage"
      class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
    >
      <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" class="text-red-600 mt-0.5" />
      <div class="flex-1">
        <div class="font-medium text-red-800">Error</div>
        <div class="text-sm text-red-700 mt-1">{{ errorMessage }}</div>
      </div>
      <button
        @click="errorMessage = ''"
        class="text-red-400 hover:text-red-600 transition-colors"
      >
        <FontAwesomeIcon :icon="['fas', 'times']" />
      </button>
    </div>

    <!-- Success Display -->
    <div
      v-if="successMessage"
      class="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
    >
      <FontAwesomeIcon :icon="['fas', 'check-circle']" class="text-green-600 mt-0.5" />
      <div class="flex-1">
        <div class="font-medium text-green-800">Success</div>
        <DemoText tag="div" class="text-sm text-green-700 mt-1" :text="successMessage" />
      </div>
      <button
        @click="successMessage = ''"
        class="text-green-400 hover:text-green-600 transition-colors"
      >
        <FontAwesomeIcon :icon="['fas', 'times']" />
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntitySearchSingle from '../widgetcomponents/Search_Widgets/EntitySearch_Single.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faUnlink } from '@fortawesome/free-solid-svg-icons';
import { isDemoMode } from '@/utils/demoSpoof';
import demoProtectedFields from '@/mixins/demoProtectedFields';
import DemoText from '@/components/DemoMode/DemoText.vue';

library.add(faUnlink);

export default {
  name: 'EntityRelationshipManager',

  components: {
    DemoText,
    FontAwesomeIcon,
    EntitySearchSingle,
    ComponentHeader,
    LoadingIndicator,
    ConfirmationModal
  },

  directives: {
    'click-outside': {
      mounted(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(); };
        document.addEventListener('click', el._clickOutside);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },
  mixins: [demoProtectedFields],
  // The parent search runs over practice / provider / facility names, which are
  // tagged. Demo Mode empties it and the tagged input is read-only. Not persisted.
  demoProtectedFields: ['entitySearch'],

  data() {
    return {
      entityTypes: [],
      selectedEntityTypeId: '',
      entitiesOfType: [],
      loadingEntitiesOfType: false,
      entitySearch: '',
      entityDropdownOpen: false,
      selectedParent: null,
      children: [],
      loadingChildren: false,
      errorMessage: '',
      successMessage: '',
      selectedChildrenForRemoval: [],
      removeTarget: null,
    };
  },

  computed: {
    filteredEntitiesOfType() {
      if (!this.entitySearch) return this.entitiesOfType;
      const q = this.entitySearch.toLowerCase();
      return this.entitiesOfType.filter(e => e.name.toLowerCase().includes(q));
    },

    // ConfirmationModal renders this as plain prose, so the entity names are
    // left out while Demo Mode is on.
    removeMessage() {
      if (!this.removeTarget || !this.selectedParent) return '';
      const demo = isDemoMode();
      const parent = demo ? 'the selected parent' : `"${this.selectedParent.name}"`;
      if (this.removeTarget.type === 'single') {
        const child = demo ? 'this entity' : `"${this.removeTarget.child.name}"`;
        return `You are removing ${child} as a child of ${parent}. The entity will not be deleted -- only the parent-child relationship is removed.`;
      }
      const count = this.removeTarget.childIds.length;
      return `You are removing ${count} ${count === 1 ? 'child' : 'children'} from ${parent}. The entities will not be deleted -- only the parent-child relationships are removed.`;
    },
  },

  watch: {
    async selectedEntityTypeId(typeId) {
      this.entitySearch = '';
      this.entityDropdownOpen = false;
      this.selectedParent = null;
      this.children = [];
      this.selectedChildrenForRemoval = [];
      this.entitiesOfType = [];
      if (!typeId) return;

      this.loadingEntitiesOfType = true;
      try {
        const response = await axios.get('/api/entities/', {
          params: { entity_type_id: typeId, limit: 1000 }
        });
        this.entitiesOfType = (response.data.results || response.data)
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        console.error('Failed to load entities:', error);
        this.entitiesOfType = [];
      } finally {
        this.loadingEntitiesOfType = false;
      }
    },
  },

  mounted() {
    this.fetchEntityTypes();
  },

  methods: {
    async fetchEntityTypes() {
      try {
        const response = await axios.get('/api/entity-types/');
        this.entityTypes = (response.data.results || response.data)
          .filter(et => et.is_active)
          .sort((a, b) => a.type_name.localeCompare(b.type_name));
      } catch (error) {
        console.error('Failed to load entity types:', error);
      }
    },

    selectParent(entity) {
      this.selectedParent = entity;
      this.entitySearch = entity.name;
      this.entityDropdownOpen = false;
      this.loadChildren();
    },

    clearParent() {
      this.selectedParent = null;
      this.entitySearch = '';
      this.children = [];
      this.selectedChildrenForRemoval = [];
      this.errorMessage = '';
      this.successMessage = '';
    },

    async loadChildren() {
      if (!this.selectedParent) return;

      this.loadingChildren = true;
      this.errorMessage = '';
      this.selectedChildrenForRemoval = [];

      try {
        // Get all entities that have this entity as a parent
        const response = await axios.get(`/api/entities/`, {
          params: {
            parent_id: this.selectedParent.id,
            include_descendants: false  // Direct children only
          }
        });

        this.children = response.data.results || response.data;
      } catch (error) {
        console.error('Failed to load children:', error);
        this.errorMessage = error.response?.data?.detail || 'Failed to load children';
        this.children = [];
      } finally {
        this.loadingChildren = false;
      }
    },

    async onChildrenSelected(entities) {
      if (!this.selectedParent) {
        this.errorMessage = 'Please select a parent entity first';
        return;
      }

      if (!entities || entities.length === 0) {
        return;
      }

      try {
        // Call the bulk-add-children endpoint
        const response = await axios.post(`/api/entities/${this.selectedParent.id}/bulk-add-children/`, {
          child_ids: entities.map(e => e.id)
        });

        const { summary, results } = response.data;

        // Build success message
        const messages = [];
        if (summary.added > 0) {
          messages.push(`Successfully added ${summary.added} ${summary.added === 1 ? 'child' : 'children'}`);
        }
        if (summary.skipped > 0) {
          messages.push(`${summary.skipped} already existed`);
        }
        if (summary.errors > 0) {
          messages.push(`${summary.errors} failed`);
        }

        if (summary.added > 0 || summary.skipped > 0) {
          this.successMessage = messages.join(', ');
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        }

        // Show errors if any
        if (results.errors && results.errors.length > 0) {
          const errorDetails = results.errors.map(e => `${e.id}: ${e.reason}`).join(', ');
          this.errorMessage = `Some entities failed: ${errorDetails}`;
        }

        // Reload children list
        await this.loadChildren();

      } catch (error) {
        console.error('Failed to add children:', error);
        this.errorMessage = error.response?.data?.error || 'Failed to add child relationships';
      }
    },

    promptRemoveChild(child) {
      this.removeTarget = { type: 'single', child };
    },

    async confirmRemove() {
      if (!this.removeTarget) return;

      if (this.removeTarget.type === 'single') {
        await this.executeRemoveChild(this.removeTarget.child);
      } else {
        await this.executeRemoveBulk(this.removeTarget.childIds);
      }
      this.removeTarget = null;
    },

    async executeRemoveChild(child) {
      try {
        await axios.post(`/api/entities/${child.id}/remove-parent/`, {
          parent_id: this.selectedParent.id
        });

        this.successMessage = `Successfully removed ${child.name} from ${this.selectedParent.name}`;
        await this.loadChildren();

        setTimeout(() => { this.successMessage = ''; }, 3000);
      } catch (error) {
        console.error('Failed to remove child:', error);
        this.errorMessage = error.response?.data?.detail || error.response?.data?.error || 'Failed to remove child relationship';
      }
    },

    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedChildrenForRemoval = this.children.map(child => child.id);
      } else {
        this.selectedChildrenForRemoval = [];
      }
    },

    toggleChildSelection(childId) {
      const index = this.selectedChildrenForRemoval.indexOf(childId);
      if (index === -1) {
        this.selectedChildrenForRemoval.push(childId);
      } else {
        this.selectedChildrenForRemoval.splice(index, 1);
      }
    },

    bulkRemoveChildren() {
      if (this.selectedChildrenForRemoval.length === 0) return;
      this.removeTarget = { type: 'bulk', childIds: [...this.selectedChildrenForRemoval] };
    },

    async executeRemoveBulk(childIds) {
      try {
        const response = await axios.post(`/api/entities/${this.selectedParent.id}/bulk-remove-children/`, {
          child_ids: childIds
        });

        const { summary, results } = response.data;

        const messages = [];
        if (summary.removed > 0) {
          messages.push(`Successfully removed ${summary.removed} ${summary.removed === 1 ? 'child' : 'children'}`);
        }
        if (summary.errors > 0) {
          messages.push(`${summary.errors} failed`);
        }

        if (summary.removed > 0) {
          this.successMessage = messages.join(', ');
          setTimeout(() => { this.successMessage = ''; }, 3000);
        }

        if (results.errors && results.errors.length > 0) {
          const errorDetails = results.errors.map(e => `${e.id}: ${e.reason}`).join(', ');
          this.errorMessage = `Some entities failed: ${errorDetails}`;
        }

        this.selectedChildrenForRemoval = [];
        await this.loadChildren();
      } catch (error) {
        console.error('Failed to remove children:', error);
        this.errorMessage = error.response?.data?.error || 'Failed to remove child relationships';
      }
    }
  }
};
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
